/**
 * usePaginationSort — client-side pagination + column sort hook.
 *
 * Usage:
 *   const { paginated, page, setPage, pageSize, setPageSize,
 *           sortKey, sortDir, handleSort, totalPages, totalCount }
 *     = usePaginationSort(data, 20)
 *
 * @param {Array}  data             — the full (filtered) dataset
 * @param {number} defaultPageSize  — initial page size (default 20)
 */

import { useState, useMemo, useEffect } from 'react'

export default function usePaginationSort(data = [], defaultPageSize = 20) {
  const [page,     setPage]     = useState(1)
  const [pageSize, setPageSize] = useState(defaultPageSize)
  const [sortKey,  setSortKey]  = useState(null)
  const [sortDir,  setSortDir]  = useState('asc')

  // Reset to page 1 whenever data length, page size, or sort changes
  useEffect(() => { setPage(1) }, [data.length, pageSize, sortKey, sortDir])

  function handleSort(key) {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
  }

  const sorted = useMemo(() => {
    if (!sortKey) return data
    return [...data].sort((a, b) => {
      const va = a[sortKey] ?? ''
      const vb = b[sortKey] ?? ''
      let cmp
      if (typeof va === 'number' && typeof vb === 'number') {
        cmp = va - vb
      } else {
        cmp = String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' })
      }
      return sortDir === 'asc' ? cmp : -cmp
    })
  }, [data, sortKey, sortDir])

  const totalCount = data.length
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))
  const safePage   = Math.min(page, totalPages)
  const paginated  = sorted.slice((safePage - 1) * pageSize, safePage * pageSize)

  return {
    // pagination
    page: safePage,
    setPage,
    pageSize,
    setPageSize,
    totalPages,
    totalCount,
    paginated,
    // sort
    sortKey,
    sortDir,
    handleSort,
    sorted,
  }
}
